import { TableCell, TableRow, Typography, Tooltip } from '@sanlam-glacier/app-common/components';
import styles from './to-do-list.module.scss';
import clsx from 'clsx';
import { useState } from 'react';
import { useSetAtom } from 'jotai';
import { selectedClientName } from 'stores/dashboard-store/dashboard-store';
import { Link } from 'react-router-dom';
import { useBreakpoint } from 'providers/breakpoint/BreakpointProvider';
import { ToDoListEntry, ToDoListTab, StatusPillColour } from 'modules/dashboard/types';

type ToDoListRowProps = {
  row: ToDoListEntry;
  selectedTab: ToDoListTab;
};

export const ToDoListRow = ({ row, selectedTab }: ToDoListRowProps) => {
  const testComponentName = 'ToDoList';

  const breakpointContext = useBreakpoint();
  const [displayTooltip, setDisplayTooltip] = useState(false);

  const setClientName = useSetAtom(selectedClientName);

  const handleClientSelection = (clientName: string) => {
    setClientName(clientName);
  };

  const handleHoverOverTableRow = () => {
    setDisplayTooltip(true);
  };

  let statusBackgroundColor = '#FFF4E7';
  let statusColor = '#FF8C00';

  switch (row.statusPillColour) {
    case StatusPillColour.blue:
      statusBackgroundColor = '#E6F1FA';
      statusColor = '#15808D';
      break;
    case StatusPillColour.amber:
      statusBackgroundColor = '#FFF4E7';
      statusColor = '#FF8C00';
      break;
    case StatusPillColour.red:
      statusBackgroundColor = '#FFF1F0';
      statusColor = '#C91400';
      break;
    default:
      break;
  }

  return (
    <TableRow
      key={row.toDoItemId}
      className={styles.tableRow}
      onMouseOver={handleHoverOverTableRow}
    >
      <TableCell className={styles.tableCells} align={TableCell.ALIGN.LEFT}>
        <Typography
          className={styles.clientName}
          variant={Typography.VARIANT.B4}
          color={Typography.COLOR.PRIMARY}
        >
          <Link
            data-testid={`${testComponentName}ViewClientId:${row.clientId}`}
            onClick={() => handleClientSelection(row.clientName)}
            to={`/clients/${row.clientId}`}
            className={styles.clientNameLink}
          >
            {row.clientName}
          </Link>
        </Typography>
      </TableCell>
      {breakpointContext.breakpoint !== 'sm' && (
        <>
          <TableCell
            className={clsx(styles.tableCells, styles.investmentNumber)}
            align={TableCell.ALIGN.LEFT}
          >
            <Typography variant={Typography.VARIANT.B4} color={Typography.COLOR.SECONDARY}>
              {row.investmentNumber}
            </Typography>
          </TableCell>
          <TableCell className={styles.tableCells} align={TableCell.ALIGN.LEFT}>
            {displayTooltip ? (
              <Tooltip position={Tooltip.POSITION.RIGHT} content={row.investmentName}>
                <span className={styles.investmentName}>
                  <Typography
                    className={styles.investmentName}
                    variant={Typography.VARIANT.B4}
                    color={Typography.COLOR.SECONDARY}
                  >
                    {row.investmentName}
                  </Typography>
                </span>
              </Tooltip>
            ) : (
              <span className={styles.investmentName}>
                <Typography
                  className={styles.investmentName}
                  variant={Typography.VARIANT.B4}
                  color={Typography.COLOR.SECONDARY}
                >
                  {row.investmentName}
                </Typography>
              </span>
            )}
          </TableCell>
        </>
      )}
      <TableCell className={styles.tableCells} align={TableCell.ALIGN.LEFT}>
        <Typography variant={Typography.VARIANT.B4} color={Typography.COLOR.PRIMARY}>
          {row.toDoItemTitle}
        </Typography>
      </TableCell>

      {selectedTab !== ToDoListTab.notCompliant && (
        <TableCell className={clsx(styles.tableCells, styles.dueDate)} align={TableCell.ALIGN.LEFT}>
          <Typography
            data-testid={`${testComponentName}DateFormat`}
            variant={Typography.VARIANT.B4}
            color={Typography.COLOR.SECONDARY}
          >
            {row.toDoItemDueDate ?? ''}
          </Typography>
        </TableCell>
      )}

      <TableCell
        className={
          selectedTab === ToDoListTab.due
            ? clsx(styles.tableCells, styles.dueStatus)
            : selectedTab === ToDoListTab.overdue
              ? clsx(styles.tableCells, styles.overdueStatus)
              : clsx(styles.tableCells, styles.notCompliantStatus)
        }
        align={TableCell.ALIGN.LEFT}
      >
        <span
          style={{ backgroundColor: statusBackgroundColor, color: statusColor }}
          className={
            selectedTab === ToDoListTab.due
              ? clsx(styles.statusPills)
              : clsx(styles.statusPills, styles.lateAndNonCompliantPills)
          }
        >
          {row.toDoItemStatus}
        </span>
      </TableCell>
    </TableRow>
  );
};
